import { Address } from '../../types/order';
import { collect } from 'collect.js';
import { AddressHelpers } from '../../services/utils/address/AddressHelpers';
import AddressFormatter from '../../services/utils/address/AddressFormatter';

export default function useGoogleMaps() {
  const extractAddressComponents = (
    place: Pick<google.maps.places.PlaceResult, 'address_components' | 'geometry'>,
  ): Address => {
    const addressComponents = collect(place.address_components);
    const location = place.geometry?.location;

    if (!addressComponents) {
      throw new Error('Cant extract address components from google place as info is not presented');
    }

    const country =
      addressComponents.first((component) => component.types.includes('country'))?.short_name ??
      null;
    const state =
      addressComponents.first((component) =>
        component.types.includes(
          country === 'GB' ? 'administrative_area_level_2' : 'administrative_area_level_1',
        ),
      )?.long_name ?? null;

    return {
      street_name:
        addressComponents.first((component) => component.types.includes('route'))?.long_name ??
        null,
      street_number:
        addressComponents.first((component) => component.types.includes('street_number'))
          ?.long_name ?? null,
      city:
        addressComponents.first(
          (component) => component.types.includes('locality'),
          addressComponents.first((component) => component.types.includes('postal_town')),
        )?.long_name ?? null,
      state,
      country,
      zip_code:
        addressComponents.first((component) => component.types.includes('postal_code'))
          ?.long_name ?? null,
      lat: location?.lat() ?? null,
      lng: location?.lng() ?? null,
    };
  };

  const matchPlaceAddress = (
    place: google.maps.places.PlaceResult,
    address: Pick<Address, 'street_name' | 'street_number' | 'city' | 'zip_code'>,
  ) => {
    return AddressHelpers.match(address, extractAddressComponents(place));
  };

  const findCoordinates = async (address: Address): Promise<Address> => {
    return new google.maps.Geocoder()
      .geocode({ address: AddressFormatter.format(address) })
      .then((result: google.maps.GeocoderResponse) => result.results)
      .then((results: google.maps.GeocoderResult[]) => {
        const location = collect(results).first(
          (item: google.maps.GeocoderResult) =>
            item.types.includes('street_address') ||
            item.types.includes('street_number') ||
            item.types.includes('neighborhood') ||
            item.types.includes('premise') ||
            item.types.includes('subpremise'),
        );

        return location
          ? extractAddressComponents(location)
          : {
              ...address,
              lat: null,
              lng: null,
            };
      });
  };

  return {
    extractAddressComponents,
    matchPlaceAddress,
    findCoordinates,
  };
}
